import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Eye, EyeOff, RefreshCw, Check, Flame, Trophy, Award, Target } from "lucide-react";
import { toast } from "sonner";
import VoiceMode from "@/pages/VoiceMode";
import LineByLineMode from "@/pages/LineByLineMode";
import TestMode from "@/pages/TestMode";
import PerformMode from "@/pages/PerformMode";
import { useStats } from "@/context/StatsContext";
import { celebrate } from "@/lib/celebrate";
import BackButton from "@/components/BackButton";

const WORD_RE = /([A-Za-z']+)/g;

function tokenize(text) {
  // returns list of {t: 'word'|'gap', v: string}
  const tokens = [];
  let last = 0;
  for (const m of text.matchAll(WORD_RE)) {
    if (m.index > last) tokens.push({ t: "sep", v: text.slice(last, m.index) });
    tokens.push({ t: "word", v: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) tokens.push({ t: "sep", v: text.slice(last) });
  return tokens;
}

function normalize(s) {
  return s.toLowerCase().replace(/[^a-z']/g, "");
}

// ---------- Fill in the Blanks ----------
function BlankMode({ sectionText, difficulty, weakWords, focusWeak, onScore }) {
  const tokens = useMemo(() => tokenize(sectionText), [sectionText]);
  const wordIdx = useMemo(() => tokens.map((t, i) => (t.t === "word" ? i : -1)).filter((i) => i >= 0), [tokens]);

  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [seed, setSeed] = useState(0);

  const blanks = useMemo(() => {
    if (focusWeak && weakWords && weakWords.length > 0) {
      const weakSet = new Set(weakWords.map((w) => normalize(w)));
      const chosen = new Set();
      for (const i of wordIdx) {
        if (weakSet.has(normalize(tokens[i].v))) chosen.add(i);
      }
      if (chosen.size === 0 && wordIdx.length > 0) chosen.add(wordIdx[Math.floor(wordIdx.length / 2)]);
      return chosen;
    }
    const rate = difficulty === "easy" ? 0.2 : difficulty === "hard" ? 0.6 : 0.4;
    const rng = mulberry32(seed + wordIdx.length);
    const chosen = new Set();
    for (const i of wordIdx) if (rng() < rate) chosen.add(i);
    if (chosen.size === 0 && wordIdx.length > 0) chosen.add(wordIdx[Math.floor(wordIdx.length / 2)]);
    return chosen;
  }, [seed, wordIdx, difficulty, focusWeak, weakWords, tokens]);

  const check = () => {
    let correct = 0;
    const missed = [];
    for (const i of blanks) {
      const ok = normalize(answers[i] || "") === normalize(tokens[i].v);
      if (ok) correct++;
      else missed.push(tokens[i].v);
    }
    const acc = blanks.size ? correct / blanks.size : 1;
    setRevealed(true);
    onScore(acc, missed);
  };

  const reset = () => {
    setAnswers({}); setRevealed(false); setSeed((s) => s + 1);
  };

  return (
    <div data-testid="blank-mode">
      <div className="flex items-center gap-2 mb-6">
        <Button size="sm" variant="outline" className="rounded-full" onClick={reset} data-testid="blank-reset-btn">
          <RefreshCw size={14} className="mr-1.5" /> New blanks
        </Button>
        {revealed ? null : (
          <Button size="sm" className="rounded-full" onClick={check} data-testid="blank-check-btn">
            <Check size={14} className="mr-1.5" /> Check answers
          </Button>
        )}
      </div>
      <div className="lyric-line text-2xl md:text-3xl leading-relaxed whitespace-pre-wrap">
        {tokens.map((t, i) => {
          if (t.t === "sep") return <span key={`sep-${i}`}>{t.v}</span>;
          if (blanks.has(i)) {
            const val = answers[i] || "";
            const correct = revealed && normalize(val) === normalize(t.v);
            const wrong = revealed && !correct;
            return (
              <input
                key={`blank-${i}`}
                data-testid={`blank-input-${i}`}
                value={revealed ? t.v : val}
                onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                disabled={revealed}
                style={{ width: `${Math.max(t.v.length + 1, 4)}ch` }}
                className={`inline-block mx-0.5 px-2 py-0.5 rounded-md bg-muted border-b-2 outline-none transition-colors
                  ${revealed ? (correct ? "border-primary text-primary" : "border-destructive text-destructive") : "border-border focus:border-primary"}`}
              />
            );
          }
          return <span key={`word-${i}`}>{t.v}</span>;
        })}
      </div>
    </div>
  );
}

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------- First Letter Mode ----------
function FirstLetterMode({ sectionText }) {
  const [reveal, setReveal] = useState(false);
  const rendered = useMemo(() => {
    return sectionText.replace(WORD_RE, (w) => (reveal ? w : w[0]));
  }, [sectionText, reveal]);
  return (
    <div data-testid="first-letter-mode">
      <div className="flex items-center gap-2 mb-6">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReveal((v) => !v)}
                data-testid="first-letter-toggle-btn">
          {reveal ? <><EyeOff size={14} className="mr-1.5" /> Hide again</> : <><Eye size={14} className="mr-1.5" /> Reveal all</>}
        </Button>
      </div>
      <p className="lyric-line text-2xl md:text-4xl leading-relaxed whitespace-pre-wrap tracking-wide">
        {rendered}
      </p>
    </div>
  );
}

// ---------- Section Recall Mode ----------
function SectionMode({ sectionText, onScore }) {
  const [attempt, setAttempt] = useState("");
  const [checked, setChecked] = useState(false);
  const [acc, setAcc] = useState(0);

  const grade = () => {
    const target = sectionText.toLowerCase().match(WORD_RE) || [];
    const guess = attempt.toLowerCase().match(WORD_RE) || [];
    if (target.length === 0) return;
    const targetCount = {};
    target.forEach((w) => { targetCount[w] = (targetCount[w] || 0) + 1; });
    const guessCount = {};
    guess.forEach((w) => { guessCount[w] = (guessCount[w] || 0) + 1; });
    let hits = 0;
    guess.forEach((w) => {
      if (targetCount[w] > 0) { hits++; targetCount[w]--; }
    });
    // Missed = target words the guess didn't cover enough of
    const missed = Object.entries(targetCount).filter(([, c]) => c > 0).map(([w]) => w);
    const a = hits / target.length;
    setAcc(a); setChecked(true); onScore(a, missed);
  };

  return (
    <div data-testid="section-mode">
      <p className="text-sm text-muted-foreground mb-4">Type this section from memory. Order matters less than getting the words right.</p>
      <textarea
        data-testid="section-attempt-input"
        value={attempt}
        onChange={(e) => setAttempt(e.target.value)}
        placeholder="Start typing…"
        className="w-full min-h-[220px] p-4 rounded-xl border border-border bg-background font-serif text-lg leading-relaxed outline-none focus:ring-2 focus:ring-primary"
      />
      <div className="mt-4 flex items-center gap-3">
        {!checked ? (
          <Button className="rounded-full" onClick={grade} data-testid="section-check-btn">Check my recall</Button>
        ) : (
          <>
            <span className="font-serif text-3xl" data-testid="section-result">{Math.round(acc * 100)}%</span>
            <Button variant="outline" className="rounded-full" onClick={() => { setAttempt(""); setChecked(false); }}
                    data-testid="section-again-btn">Try again</Button>
          </>
        )}
      </div>
      {checked && (
        <div className="mt-6 p-4 bg-muted rounded-xl">
          <p className="overline-label mb-2">Original</p>
          <pre className="whitespace-pre-wrap font-serif text-lg leading-relaxed">{sectionText}</pre>
        </div>
      )}
    </div>
  );
}

// ---------- Main Practice Page ----------
export default function Practice() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [song, setSong] = useState(null);
  const [stage, setStage] = useState("play"); // play | test | perform
  const [sectionIdx, setSectionIdx] = useState(0);
  const [mode, setMode] = useState("blanks");
  const [difficulty, setDifficulty] = useState("auto");
  const [focusWeak, setFocusWeak] = useState(false);
  const [hint, setHint] = useState("");
  const [hintLoading, setHintLoading] = useState(false);
  const [lastScore, setLastScore] = useState(null);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const statsCtx = useStats();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/songs/${id}`);
        setSong(data);
        // Cold-call: ?section=N points at song.sections[N]; sections[0] is now Whole song, so add 1
        const s = params.get("section");
        if (s !== null && !isNaN(Number(s))) {
          const raw = Number(s);
          const total = (data.sections?.length || 0) + 1; // +1 for Whole song
          const n = Math.max(0, Math.min(total - 1, raw + 1));
          setSectionIdx(n);
        }
      } catch (e) { toast.error(formatApiError(e)); nav("/library"); }
    })();
  }, [id, nav, params]);

  const sections = useMemo(() => {
    if (!song) return [];
    const parts = song.sections?.length ? song.sections : [];
    return [{ name: "Whole song", text: song.lyrics }, ...parts];
  }, [song]);
  const current = sections[sectionIdx];

  // Auto difficulty ramps with mastery: 0-30% → easy, 30-70% → medium, >70% → hard
  const effectiveDifficulty = useMemo(() => {
    if (difficulty !== "auto") return difficulty;
    const m = song?.mastery || 0;
    if (m < 30) return "easy";
    if (m < 70) return "medium";
    return "hard";
  }, [difficulty, song]);

  const recordScore = async (accuracy, missedWords = []) => {
    setLastScore(accuracy);
    const nextCombo = accuracy >= 0.7 ? combo + 1 : 0;
    setCombo(nextCombo);
    setBestCombo((b) => Math.max(b, nextCombo));
    try {
      const { data } = await api.post(`/songs/${id}/practice`, {
        mode, accuracy, section_name: current?.name, combo: nextCombo,
        missed_words: missedWords,
      });
      toast.success(`+${data.xp_gain} XP · Mastery ${Math.round(data.mastery)}%`);
      if (data.level_up) {
        celebrate("big");
        toast("Level up!", { description: `You reached level ${data.level}.` });
      } else if (accuracy >= 0.999) {
        celebrate("normal");
      } else if (nextCombo > 0 && nextCombo % 3 === 0) {
        celebrate("small");
        toast(`${nextCombo}× combo`, { description: "Keep going." });
      }
      if (data.unlocked && data.unlocked.length) {
        data.unlocked.forEach((a) =>
          toast(`Unlocked: ${a.title}`, { description: a.desc, icon: null })
        );
        celebrate("big");
      }
      // refresh song (for weak_words) + header stats
      try {
        const { data: fresh } = await api.get(`/songs/${id}`);
        setSong(fresh);
      } catch (err) { console.warn("post-practice song reload failed", err); }
      statsCtx?.refresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearWeakWords = async () => {
    try {
      await api.post(`/songs/${id}/clear-weak-words`);
      const { data: fresh } = await api.get(`/songs/${id}`);
      setSong(fresh);
      setFocusWeak(false);
      toast.success("Weak spots cleared.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const getHint = async () => {
    if (!current) return;
    setHintLoading(true); setHint("");
    try {
      const { data } = await api.post("/ai/hint", { song_id: id, section_name: current.name });
      setHint(data.hint || "");
      if (data.unlocked && data.unlocked.length) {
        data.unlocked.forEach((a) =>
          toast(`Unlocked: ${a.title}`, { description: a.desc })
        );
        statsCtx?.refresh?.();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHintLoading(false); }
  };

  if (!song) return <main className="max-w-3xl mx-auto px-6 py-16 text-muted-foreground" data-testid="practice-loading">Loading…</main>;

  return (
    <main data-testid="practice-page" className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <BackButton to={`/songs/${id}`} label="Back to song" testid="practice-back-btn" />

      <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="overline-label text-accent">Practice</p>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tight" data-testid="practice-title">{song.title}</h1>
          <p className="text-muted-foreground">{song.artist}</p>
          {stage === "play" && (combo > 0 || bestCombo > 0) && (
            <div className="mt-3 flex items-center gap-3 text-sm" data-testid="practice-combo">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground font-medium">
                <Flame size={14} /> {combo}× combo
              </span>
              {bestCombo > combo && (
                <span className="text-muted-foreground">Best this session: {bestCombo}×</span>
              )}
            </div>
          )}
        </div>

        {/* Stage switcher */}
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" data-testid="stage-switcher">
          {[
            { id: "play", label: "Play" },
            { id: "test", label: "Test" },
            { id: "perform", label: "Perform" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStage(s.id)}
              className={`px-4 h-9 text-sm rounded-full transition-colors ${
                stage === s.id
                  ? "bg-primary text-primary-foreground glow-amber-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`stage-${s.id}-btn`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stage description strip */}
      <p className="mt-3 text-sm text-muted-foreground" data-testid="stage-hint">
        {stage === "play"
          ? "Learn it here — pick a drill, get hints, no pressure."
          : stage === "test"
          ? "Grade yourself — no hints, one final score."
          : "Take the stage — big teleprompter, backing track, no distractions."}
      </p>

      {/* TEST stage */}
      {stage === "test" && (
        <div className="mt-8 border border-border rounded-2xl bg-card p-6 md:p-10 grain-panel" data-testid="stage-test">
          <TestMode songId={id} sections={sections} onFinish={(_, jumpTo) => { if (jumpTo === "perform") setStage("perform"); }} />
        </div>
      )}

      {/* PERFORM stage */}
      {stage === "perform" && (
        <div className="mt-8" data-testid="stage-perform">
          <PerformMode song={song} />
        </div>
      )}

      {/* PLAY stage (default) */}
      {stage === "play" && (
      <>
      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="play-controls">
          <Select value={String(sectionIdx)} onValueChange={(v) => setSectionIdx(Number(v))}>
            <SelectTrigger className="w-[180px] rounded-full h-10" data-testid="practice-section-select">
              <SelectValue placeholder="Section" />
            </SelectTrigger>
            <SelectContent>
              {sections.map((s, i) => (
                <SelectItem key={`${s.name}-${i}`} value={String(i)} data-testid={`practice-section-option-${i}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {mode === "blanks" && (
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger className="w-[140px] rounded-full h-10" data-testid="practice-difficulty-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto" data-testid="difficulty-auto">Auto (adaptive)</SelectItem>
                <SelectItem value="easy" data-testid="difficulty-easy">Easy</SelectItem>
                <SelectItem value="medium" data-testid="difficulty-medium">Medium</SelectItem>
                <SelectItem value="hard" data-testid="difficulty-hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

      {/* Weak-spot control (only in Blanks mode) */}
      {mode === "blanks" && (
        <div className="mt-6 flex items-center justify-between gap-4 border border-border rounded-2xl px-5 py-3 bg-secondary/40"
             data-testid="weak-spot-panel">
          <div className="flex items-center gap-3">
            <Target size={16} className="text-primary" />
            <div>
              <p className="text-sm font-medium">Focus on your weak spots</p>
              <p className="text-xs text-muted-foreground">
                {song.weak_words?.length
                  ? `${song.weak_words.length} tricky word${song.weak_words.length === 1 ? "" : "s"} being tracked.`
                  : "Miss a few words and they'll show up here to drill."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {song.weak_words?.length > 0 && (
              <button
                onClick={clearWeakWords}
                className="text-xs text-muted-foreground hover:text-foreground underline"
                data-testid="clear-weak-words-btn"
              >
                Clear
              </button>
            )}
            <Switch
              checked={focusWeak}
              onCheckedChange={setFocusWeak}
              disabled={!song.weak_words?.length}
              data-testid="focus-weak-switch"
            />
          </div>
        </div>
      )}

      <Tabs value={mode} onValueChange={setMode} className="mt-8">
        <p className="overline-label mb-3" data-testid="pick-drill-label">Pick a drill</p>
        <TabsList className="rounded-full h-11 p-1 bg-secondary flex-wrap" data-testid="practice-tabs">
          <TabsTrigger value="blanks" className="rounded-full px-4" data-testid="tab-blanks">Blanks</TabsTrigger>
          <TabsTrigger value="line" className="rounded-full px-4" data-testid="tab-line">Line by line</TabsTrigger>
          <TabsTrigger value="first-letter" className="rounded-full px-4" data-testid="tab-first-letter">First letter</TabsTrigger>
          <TabsTrigger value="section" className="rounded-full px-4" data-testid="tab-section">Section recall</TabsTrigger>
          <TabsTrigger value="voice" className="rounded-full px-4" data-testid="tab-voice">Sing it</TabsTrigger>
        </TabsList>

        <div className="mt-8 border border-border rounded-2xl bg-background p-6 md:p-10">
          <p className="overline-label mb-4">{current?.name}</p>
          <TabsContent value="blanks" forceMount={mode === "blanks" ? true : undefined} hidden={mode !== "blanks"}>
            {mode === "blanks" && current && (
              <BlankMode
                sectionText={current.text}
                difficulty={effectiveDifficulty}
                weakWords={song.weak_words || []}
                focusWeak={focusWeak && (song.weak_words || []).length > 0}
                onScore={(acc, missed) => recordScore(acc, missed)}
              />
            )}
          </TabsContent>
          <TabsContent value="first-letter" forceMount={mode === "first-letter" ? true : undefined} hidden={mode !== "first-letter"}>
            {mode === "first-letter" && current && <FirstLetterMode sectionText={current.text} />}
          </TabsContent>
          <TabsContent value="line" forceMount={mode === "line" ? true : undefined} hidden={mode !== "line"}>
            {mode === "line" && current && (
              <LineByLineMode
                sectionText={current.text}
                onScore={(acc, missed) => recordScore(acc, missed)}
              />
            )}
          </TabsContent>
          <TabsContent value="section" forceMount={mode === "section" ? true : undefined} hidden={mode !== "section"}>
            {mode === "section" && current && (
              <SectionMode sectionText={current.text} onScore={(acc, missed) => recordScore(acc, missed)} />
            )}
          </TabsContent>
          <TabsContent value="voice" forceMount={mode === "voice" ? true : undefined} hidden={mode !== "voice"}>
            {mode === "voice" && current && (
              <VoiceMode
                songId={id}
                sectionText={current.text}
                hasBackingTrack={!!song.backing_track_path}
                onScore={(acc, missed) => recordScore(acc, missed)}
              />
            )}
          </TabsContent>
        </div>
      </Tabs>

      {/* AI Coach */}
      <div className="mt-8 border border-border rounded-2xl p-6 bg-secondary/40">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="overline-label">AI Coach</p>
            <p className="font-serif text-2xl tracking-tight mt-1">Stuck? Get one gentle hint.</p>
          </div>
          <Button onClick={getHint} disabled={hintLoading} className="rounded-full" data-testid="practice-hint-btn">
            <Sparkles size={14} className="mr-1.5" /> {hintLoading ? "Thinking…" : "Give me a hint"}
          </Button>
        </div>
        {hint && (
          <div className="mt-4 p-4 bg-background border border-border rounded-xl font-serif text-lg leading-relaxed"
               data-testid="practice-hint-result">
            {hint}
          </div>
        )}
      </div>

      {lastScore !== null && (
        <p className="mt-6 text-sm text-muted-foreground" data-testid="practice-last-score">
          Last check: <span className="text-foreground font-medium">{Math.round(lastScore * 100)}%</span>
        </p>
      )}
      </>
      )}
    </main>
  );
}
